/**
 * PublishContext
 *
 * Holds the cross-tab state for the unified YouTube page: which top sub-tab is
 * active (download / generate) and, inside "generate", which destination the
 * user is preparing for (Instagram vs YouTube). The post-generation CTA in
 * ResultDisplay drives both via custom events so the user lands directly on
 * "generate → YouTube" with the run pre-filled.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { isServerlessMode } from '../../utils/apiMode';
import {
  peekPendingPublishPreset,
  subscribePendingPublishPreset,
} from '../../components/youtube-publisher/pendingPublishPreset';
import {
  PublishContext,
  type PublishContextValue,
  type PublishTab,
  type MediaTab,
} from './PublishContext.context';

export function PublishProvider({ children }: { children: React.ReactNode }) {
  // 'download' (yt-dlp) is desktop-only; web defaults to the generate flow.
  const [tab, setTab] = useState<PublishTab>(
    isServerlessMode() || peekPendingPublishPreset() !== null ? 'generate' : 'download'
  );
  // YouTube is the primary destination (post-generation publish); Instagram is opt-in.
  const [mediaTab, setMediaTab] = useState<MediaTab>('youtube');

  // A finished generation was handed to the publish form while this page is
  // already mounted: land on the form (generate -> YouTube). When the page mounts
  // because of that hand-off, the initial state above already does this.
  useEffect(
    () =>
      subscribePendingPublishPreset(() => {
        setTab('generate');
        setMediaTab('youtube');
      }),
    []
  );

  // Programmatic navigation (e.g. ResultDisplay CTA): switch the top sub-tab.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<PublishTab>).detail;
      if (detail === 'download' || detail === 'generate') {
        setTab(detail);
      }
    };
    window.addEventListener('set-youtube-sub-tab', handler);
    return () => window.removeEventListener('set-youtube-sub-tab', handler);
  }, []);

  // Programmatic navigation: switch the Instagram/YouTube destination.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<MediaTab>).detail;
      if (detail === 'instagram' || detail === 'youtube') {
        setMediaTab(detail);
      }
    };
    window.addEventListener('set-youtube-media-tab', handler);
    return () => window.removeEventListener('set-youtube-media-tab', handler);
  }, []);

  const value = useMemo<PublishContextValue>(
    () => ({ tab, setTab, mediaTab, setMediaTab }),
    [tab, mediaTab]
  );

  return <PublishContext.Provider value={value}>{children}</PublishContext.Provider>;
}
